import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { ReportData } from "../types";
import { fmt } from "../utils/format";
import { MONTH_STATUS_LABEL } from "../utils/reportSections";

interface Props {
  report: ReportData;
  busy: boolean;
  onClose: () => void;
  onReopen: () => void;
}

const STATUS_COLOR = { open: "default", reconciled: "success", closed: "primary" } as const;

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) : "";

/** Month status (Open / Reconciled / Closed), why it can't be closed yet, and Close / Reopen. Monthly view only. */
const MonthStatusCard: React.FC<Props> = ({ report, busy, onClose, onReopen }) => {
  const [confirm, setConfirm] = useState<"close" | "reopen" | null>(null);
  const monthly = report.monthly!;
  const { month, closeBlockers, permissions, laterClosedMonths } = monthly;
  const closed = month.status === "closed";

  return (
    <Card elevation={2} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          Month Status
        </Typography>
        <Chip label={MONTH_STATUS_LABEL[month.status]} color={STATUS_COLOR[month.status]} icon={closed ? <LockIcon /> : undefined} />
        {closed && (
          <Typography variant="body2" color="text.secondary">
            Closed by {month.closedBy ?? "—"} on {when(month.closedAt)} · Closing balance {fmt(month.closingBalance ?? 0)}
          </Typography>
        )}
        {!closed && month.reopenedAt && (
          <Typography variant="body2" color="text.secondary">
            Reopened by {month.reopenedBy ?? "—"} on {when(month.reopenedAt)}
          </Typography>
        )}

        <Box display="flex" gap={1} flexWrap="wrap" sx={{ ml: { md: "auto" } }}>
          {!closed && permissions.canClose && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<LockIcon />}
              disabled={busy || closeBlockers.length > 0}
              onClick={() => setConfirm("close")}
            >
              Close Month
            </Button>
          )}
          {closed && permissions.canReopen && (
            <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} disabled={busy} onClick={() => setConfirm("reopen")}>
              Reopen Month
            </Button>
          )}
        </Box>
      </Box>

      {!closed && closeBlockers.length > 0 && permissions.canClose && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Before this month can be closed:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {closeBlockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </Box>
        </Alert>
      )}
      {month.stale && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Transactions in this closed month were changed outside the app — the live figures no longer match what was
          closed. Later months still carry forward the closed balance. Reopen the month to review.
        </Alert>
      )}

      <Dialog open={confirm === "close"} onClose={() => setConfirm(null)}>
        <DialogTitle>Close {report.period.label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The closing balance of {fmt(monthly.reconciliation.actual ?? 0)} will be frozen and used as next month's carry
            forward. Income, expenses and deposits dated in this month can't be changed until it is reopened.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirm(null);
              onClose();
            }}
          >
            Close Month
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === "reopen"} onClose={() => setConfirm(null)}>
        <DialogTitle>Reopen {report.period.label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>Reopening this month may affect future carry-forward balances.</DialogContentText>
          {laterClosedMonths.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Later months are already closed: <b>{laterClosedMonths.join(", ")}</b>. They keep their frozen balances and
              will NOT be recalculated — if you change this month, review and reopen those months too.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirm(null);
              onReopen();
            }}
          >
            Reopen Month
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MonthStatusCard;
